import React, { useRef, useState, useMemo } from 'react';
import classNames from 'classnames';
import Row from '../Row';
import Col from '../Col';
import FontAwesomeIcon from '../Icon';
import Checkbox from '../Checkbox';
import SignatureField from './SignatureField/SignatureField';
import { SignMode, SignedType } from './types';
import { Images } from 'src/Tenants';
import { useBranding } from 'src/branding/BrandingContextProvider';
import { ROLES } from 'src/utils/MessagingUtils/shared-constants';
import './SignatureCanvas.scss';

interface SignatureCanvasProps {
  onInsert: (image: string, signType: SignedType) => void;
  remove: () => void;
  signed?: string;
  signType?: string;
  firstName?: string;
  lastName?: string;
  type?: string;
  typeOfSign?: string;
  permissions?: string;
  signeeRole?: string;
  onClose?: () => void;
}

interface ConfirmationTextBranding {
  text?: {
    keyPickupConfirmation?: string;
    propertyDeveloperKeyPickupConfirmation?: string;
  };
}

interface ConfirmationTextParams {
  type?: string;
  typeOfSign?: string;
  permissions?: string;
  signeeRole?: string;
  branding: ConfirmationTextBranding | null | undefined;
}

const resolveConfirmationText = ({
  type,
  typeOfSign,
  permissions,
  signeeRole,
  branding,
}: ConfirmationTextParams): string => {
  if (type === 'keyhandoff' && signeeRole === ROLES.PROPERTY_DEVELOPER) {
    return branding?.text?.propertyDeveloperKeyPickupConfirmation;
  }

  if (type === 'keyhandoff') {
    return branding?.text?.keyPickupConfirmation;
  }

  const isHomeOwnerWalkThrough: boolean =
    typeOfSign === 'Home Owner Walk-through' ||
    ((typeOfSign === 'Simplified' || typeOfSign === 'Enhanced') &&
      permissions === 'Homeowner or Occupant');

  if (isHomeOwnerWalkThrough) {
    return Images.completeHomeOwnerWalkThroughSignatureTermsAndConditions;
  }

  return Images.completeWalkThroughSignatureTermsAndConditions;
};

const SignatureCanvas = ({
  onInsert,
  remove,
  signed = '',
  signType: signedSignType = '',
  firstName = '',
  lastName = '',
  type,
  typeOfSign,
  permissions,
  signeeRole,
  onClose,
}: SignatureCanvasProps) => {
  const sigPadRef = useRef<any>(null);
  const { branding } = useBranding();

  const [signMode, setSignMode] = useState<SignMode>(SignMode.Draw);
  const [trimmedDataURL, setTrimmedDataURL] = useState<string | null>(null);
  const [signText, setSignText] = useState<string>('');
  const [acceptTermsConditions, setAcceptTermsConditions] =
    useState<boolean>(false);

  const isEditing: boolean = signed === '';

  const clear = () => {
    if (signMode === SignMode.Draw) {
      sigPadRef.current?.clear();
      setTrimmedDataURL(null);
      return;
    }

    setSignText('');
  };

  const removeSign = () => {
    setTrimmedDataURL(null);
    setSignMode(SignMode.Draw);
    setSignText('');
    setAcceptTermsConditions(false);

    remove();
  };

  const insert = () => {
    if (signMode === SignMode.Draw) {
      const dataUrl: string = sigPadRef.current
        .getTrimmedCanvas()
        .toDataURL('image/png');

      setTrimmedDataURL(dataUrl);
      onInsert(dataUrl, 'signed');
      return;
    }

    onInsert(signText, 'text');
  };

  const handleDrawEnd = () => {
    setTrimmedDataURL(
      sigPadRef.current.getTrimmedCanvas().toDataURL('image/png')
    );
  };

  let isInsertDisabled: boolean = true;

  if (signMode === SignMode.Draw) {
    isInsertDisabled = trimmedDataURL === null;
  } else if (signMode === SignMode.Type) {
    isInsertDisabled = signText === '';
  }

  const confirmationText: string = useMemo(
    () =>
      resolveConfirmationText({
        type,
        typeOfSign,
        permissions,
        signeeRole,
        branding,
      }),
    [type, typeOfSign, permissions, signeeRole, branding]
  );

  return (
    <div className="signatureCanvas">
      <Row mdCenter>
        <Col>
          <div className="signatureCanvas__options">
            <div
              className={classNames('signatureCanvas__options__option', {
                signatureCanvas__options__option__signSelected:
                  signMode === SignMode.Draw,
              })}
              onClick={() => {
                if (isEditing) {
                  setSignMode(SignMode.Draw);
                }
              }}
            >
              <FontAwesomeIcon glyph="signature" weight="solid" />
            </div>
            <div
              className={classNames('signatureCanvas__options__option', {
                signatureCanvas__options__option__signSelected:
                  signMode === SignMode.Type,
              })}
              onClick={() => {
                if (isEditing) {
                  setSignMode(SignMode.Type);
                  setSignText(firstName + ' ' + lastName);
                }
              }}
            >
              <FontAwesomeIcon glyph="text" weight="regular" />
            </div>
          </div>
        </Col>
        <Col xs="10" md="10">
          <div
            className={classNames('signatureCanvas__container', {
              'is-signed': !isEditing,
            })}
          >
            <div style={{ textAlign: 'right' }}>
              <button
                className="signatureCanvas__buttons"
                onClick={() => (isEditing ? clear() : removeSign())}
              >
                {isEditing ? 'Clear' : 'Remove'}
              </button>
            </div>
            <div className="signatureCanvas__sigContainer">
              <SignatureField
                signed={signed}
                signedSignType={signedSignType}
                signMode={signMode}
                signText={signText}
                onChangeSignText={(value: string) => setSignText(value)}
                setSigPad={(ref: any) => {
                  sigPadRef.current = ref;
                }}
                onDrawEnd={handleDrawEnd}
              />
            </div>
          </div>
        </Col>
        <div className="signatureCanvas__insertBox">
          <div style={{ display: 'flex', width: '90%' }}>
            <div
              className="signatureCanvas__insertBox__backButton"
              onClick={() => {
                if (isEditing && onClose) {
                  onClose();
                }
              }}
            >
              <div style={{ paddingTop: 0.5, paddingRight: 5 }}>
                <FontAwesomeIcon
                  glyph="chevron-left"
                  weight="solid"
                  size="small"
                />
              </div>
              Back
            </div>
            <div className="signatureCanvas__insertBox__legalNote">
              I understand this is a legal representation of my signature
            </div>
          </div>
          <div className="signatureCanvas__insertBox__InsertButton">
            <div
              className={classNames(
                'signatureCanvas__insertBox__InsertButton__button',
                {
                  signatureCanvas__insertBox__InsertButton__button__disabled:
                    isInsertDisabled,
                }
              )}
              onClick={() => {
                if (!isEditing) {
                  return;
                }

                if (acceptTermsConditions) {
                  insert();
                } else {
                  alert('Please check terms and condition check box');
                }
              }}
            >
              {isEditing ? 'INSERT' : 'Added'}
            </div>
          </div>
        </div>
        <div style={{ fontSize: 20, padding: 20, paddingBottom: '5em' }}>
          <Checkbox
            label={confirmationText}
            value={signed !== '' ? true : acceptTermsConditions}
            onChange={() => {
              if (isEditing) {
                setAcceptTermsConditions((prev: boolean) => !prev);
              }
            }}
          />
        </div>
      </Row>
    </div>
  );
};

export default SignatureCanvas;